import { Component, OnInit } from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from "@angular/forms";
import Swal from "sweetalert2";
import { catchError, of } from "rxjs";
  
import { Utils } from "../../../../../shared/utils/Utils";
import {
  PesvDesplazamientos,
  PesvDesplazamientosPayload,
  PesvDesplazamientosService,
} from "../../../../../shared/services/pesvDesplazamientos.service";
import { PropietarioService } from "../../../../../shared/services/propietarios.service";
// ⚠️ Ajusta la ruta/nombre del servicio de vehículos a los de tu proyecto
import { VehiclesService } from "../../../../../shared/services/vehicles.service";
import { DataService } from "../../../../../shared/services/municipios.service";
import { TipoViaService } from "../../../../../shared/services/tipo-via.service";
import { Router } from "@angular/router";

/** Valida que el kilometraje final no sea menor al inicial */
function kilometrajeValidator(group: AbstractControl): ValidationErrors | null {
  const ini = group.get("kilometraje_inicial")?.value;
  const fin = group.get("kilometraje_final")?.value;
  const hayIni = ini !== null && ini !== undefined && ini !== "";
  const hayFin = fin !== null && fin !== undefined && fin !== "";
  if (hayIni && hayFin && Number(fin) < Number(ini)) {
    return { kmInvalido: true };
  }
  return null;
}

@Component({
  selector: "app-desplazamientos",
  templateUrl: "./desplazamientos.component.html",
  styleUrls: ["./desplazamientos.component.scss"],
})
export class DesplazamientosComponent implements OnInit {
  // ============ TABS PRINCIPALES (formulario / listado) ============
  activeTab = 1;

  desplazamientoForm!: FormGroup;
  guardando = false;
  modoEdicion = false;
  idDesplazamientoEditando: number | null = null;

  // Catálogos
  colaboradores: any[] = [];
  cargandoColaboradores = false;
  vehiculos: any[] = [];
  cargandoVehiculos = false;
  tiposVia: any[] = [];

  // Listado
  desplazamientos: PesvDesplazamientos[] = [];
  desplazamientosFiltrados: PesvDesplazamientos[] = [];
  cargandoListado = false;
  filtro = "";
  filtroEstado: number | null = null;
  page = 1;
  pageSize = 10;
  municipios: any[] = [];

  constructor(
    private fb: FormBuilder,
    private desplazamientosService: PesvDesplazamientosService,
    private propietarioService: PropietarioService,
    private vehiclesService: VehiclesService,
    private dataService: DataService,
    private tipoViaService: TipoViaService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.inicializarFormulario();
    this.cargarColaboradores();
    this.cargarVehiculos();
    this.cargarDesplazamientos();
    this.cargarMunicipios();
    this.cargarTiposVia();
  }

  private inicializarFormulario(): void {
    this.desplazamientoForm = this.fb.group(
      {
        n_documento: ["", Validators.required],
        fecha: ["", Validators.required],
        conductor_id: [null],
        vehiculo_id: [null],
        origen: [null, Validators.required],
        destino: [null, Validators.required],
        ruta: [null],
        hora_salida_programada: [""],
        hora_llegada_programada: [""],
        hora_salida_real: [""],
        hora_llegada_real: [""],
        motivo: [""],
        kilometraje_inicial: [null, [Validators.min(0)]],
        kilometraje_final: [null, [Validators.min(0)]],
        observaciones: [""],
      },
      { validators: kilometrajeValidator },
    );
  }

  // ============ Catálogos ============
  private cargarColaboradores(): void {
    this.cargandoColaboradores = true;

    this.propietarioService
      .getPlanAccion()
      .pipe(catchError(() => of([])))
      .subscribe({
        next: (response: any) => {
          this.colaboradores = this.normalizarLista(response).map((c: any) => ({
            ...c,
            nombreCompleto: c.nombreCompleto ?? "",
            cargo: c.cargoData?.cargos ?? "",
          }));
          this.cargandoColaboradores = false;
        },
        error: () => {
          this.colaboradores = [];
          this.cargandoColaboradores = false;
        },
      });
  }

  private cargarVehiculos(): void {
    this.cargandoVehiculos = true;

    this.vehiclesService
      .getPlacasAll()
      .pipe(catchError(() => of([])))
      .subscribe({
        next: (response: any) => {
          this.vehiculos = Array.isArray(response?.vehicles)
            ? response.vehicles
            : this.normalizarLista(response);
          this.cargandoVehiculos = false;
        },
        error: () => {
          this.vehiculos = [];
          this.cargandoVehiculos = false;
        },
      });
  }

  private cargarMunicipios(): void {
    this.dataService
      .getDatos()
      .pipe(catchError(() => of([])))
      .subscribe({
        next: (response: any) => {
          this.municipios = this.normalizarLista(response);
        },
        error: () => {
          this.municipios = [];
        },
      });
  }

  private cargarTiposVia(): void {
    this.tipoViaService
      .getTiposVia()
      .pipe(catchError(() => of([])))
      .subscribe({
        next: (response: any) => {
          this.tiposVia = Array.isArray(response?.tiposVia)
            ? response.tiposVia
            : this.normalizarLista(response);
        },
        error: () => {
          this.tiposVia = [];
        },
      });
  }


  private normalizarLista(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    return [];
  }

  // ============ Helpers de presentación ============
  nombreConductor(id?: number | null): string {
    if (!id) return "-";
    const c = this.colaboradores.find((x) => x.id === id);
    return c?.nombreCompleto || "-";
  }

  placaVehiculo(id?: string | number | null): string {
    if (id === null || id === undefined || id === "") return "-";
    const v = this.vehiculos.find((x) => String(x.placa) === String(id));
    return v?.placa || "-";
  }

  kmRecorridos(d: PesvDesplazamientos): string {
    const ini = d.kilometraje_inicial;
    const fin = d.kilometraje_final;
    if (ini === null || ini === undefined || fin === null || fin === undefined) return "-";
    return String(Number(fin) - Number(ini));
  }

  // ============ Listado ============
  cargarDesplazamientos(): void {
    this.cargandoListado = true;
    this.desplazamientosService.getAll().subscribe({
      next: (data: any) => {
        this.desplazamientos = this.normalizarLista(data);
        this.aplicarFiltro();
        this.cargandoListado = false;
      },
      error: () => {
        this.desplazamientos = [];
        this.desplazamientosFiltrados = [];
        this.cargandoListado = false;
      },
    });
  }

  aplicarFiltro(): void {
    const termino = this.filtro.toLowerCase().trim();
    this.desplazamientosFiltrados = this.desplazamientos.filter((d) => {
      const coincideTexto =
        !termino ||
        [
          d.n_documento,
          d.origen,
          d.destino,
          d.ruta,
          this.nombreConductor(d.conductor_id),
          this.placaVehiculo(d.vehiculo_id),
        ]
          .filter((v) => v && v !== "-")
          .some((v) => String(v).toLowerCase().includes(termino));

      const coincideEstado = this.filtroEstado === null || d.estado === this.filtroEstado;
      return coincideTexto && coincideEstado;
    });
    this.page = 1;
  }

  // ============ Guardar / editar ============
  private aNumeroONull(valor: any): number | null {
    return valor === null || valor === undefined || valor === "" ? null : Number(valor);
  }

  private aTextoONull(valor: any): string | null {
    if (valor === null || valor === undefined) return null;
    const t = String(valor).trim();
    return t === "" ? null : t;
  }

  private construirPayload(): PesvDesplazamientosPayload {
    const raw = this.desplazamientoForm.getRawValue();
    return {
      n_documento: String(raw.n_documento).trim(),
      fecha: raw.fecha,
      conductor_id: this.aNumeroONull(raw.conductor_id),
      vehiculo_id: this.aTextoONull(raw.vehiculo_id),
      origen: String(raw.origen).trim(),
      destino: String(raw.destino).trim(),
      ruta: this.aTextoONull(raw.ruta),
      hora_salida_programada: this.aTextoONull(raw.hora_salida_programada),
      hora_llegada_programada: this.aTextoONull(raw.hora_llegada_programada),
      hora_salida_real: this.aTextoONull(raw.hora_salida_real),
      hora_llegada_real: this.aTextoONull(raw.hora_llegada_real),
      motivo: this.aTextoONull(raw.motivo),
      kilometraje_inicial: this.aNumeroONull(raw.kilometraje_inicial),
      kilometraje_final: this.aNumeroONull(raw.kilometraje_final),
      observaciones: this.aTextoONull(raw.observaciones),
    };
  }

  guardar(): void {
    if (
      this.modoEdicion &&
      Number(this.desplazamientos.find((item) => item.id === this.idDesplazamientoEditando)?.estado) !== 1
    ) {
      Swal.fire("No se puede editar", "Solo se pueden editar desplazamientos activos.", "warning");
      return;
    }

    if (this.desplazamientoForm.invalid) {
      this.desplazamientoForm.markAllAsTouched();
      const mensaje = this.desplazamientoForm.hasError("kmInvalido")
        ? "El kilometraje final no puede ser menor al inicial."
        : "Completa todos los campos requeridos.";
      Swal.fire("Campos obligatorios", mensaje, "warning");
      return;
    }

    const payload = this.construirPayload();
    this.guardando = true;

    const obs =
      this.modoEdicion && this.idDesplazamientoEditando
        ? this.desplazamientosService.update(this.idDesplazamientoEditando, payload)
        : this.desplazamientosService.create({ ...payload, estado: 1 });

    obs.subscribe({
      next: () => {
        this.guardando = false;
        Swal.fire("Éxito", "Desplazamiento guardado correctamente.", "success");
        this.cancelarEdicion();
        this.cargarDesplazamientos();
        this.activeTab = 2;
      },
      error: (error) => {
        this.guardando = false;
        Swal.fire(
          "Error",
          error?.error?.message || error?.error?.error || "No se pudo guardar el desplazamiento.",
          "error",
        );
      },
    });
  }

  editar(desplazamiento: PesvDesplazamientos): void {
    if (Number(desplazamiento.estado) !== 1) {
      Swal.fire("No se puede editar", "Solo se pueden editar desplazamientos activos.", "warning");
      return;
    }

    if (!desplazamiento.id) return;

    this.desplazamientosService.getById(desplazamiento.id).subscribe({
      next: (respuesta: any) => {
        const datos = respuesta?.data ?? respuesta;
        this.modoEdicion = true;
        this.idDesplazamientoEditando = Number(datos.id ?? desplazamiento.id);
        this.desplazamientoForm.patchValue({
          n_documento: datos.n_documento,
          fecha: datos.fecha?.substring(0, 10),
          conductor_id: datos.conductor_id ?? null,
          vehiculo_id: typeof datos.vehiculo_id === "object"
            ? datos.vehiculo_id?.placa ?? null
            : datos.vehiculo_id ?? null,
          origen: datos.origen,
          destino: datos.destino,
          ruta: datos.ruta ?? "",
          hora_salida_programada: datos.hora_salida_programada?.substring(0, 5) ?? "",
          hora_llegada_programada: datos.hora_llegada_programada?.substring(0, 5) ?? "",
          hora_salida_real: datos.hora_salida_real?.substring(0, 5) ?? "",
          hora_llegada_real: datos.hora_llegada_real?.substring(0, 5) ?? "",
          motivo: datos.motivo ?? "",
          kilometraje_inicial: datos.kilometraje_inicial ?? null,
          kilometraje_final: datos.kilometraje_final ?? null,
          observaciones: datos.observaciones ?? "",
        });
        this.activeTab = 1;
      },
      error: () => Swal.fire("Error", "No se pudieron cargar los datos del desplazamiento.", "error"),
    });
  }

  cancelarEdicion(): void {
    this.modoEdicion = false;
    this.idDesplazamientoEditando = null;
    this.desplazamientoForm.reset();
  }

  // ============ Activar / Inactivar ============
  inactivar(desplazamiento: PesvDesplazamientos): void {
    Swal.fire({
      title: "¿Inactivar desplazamiento?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, inactivar",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (!result.isConfirmed || !desplazamiento.id) return;
      this.desplazamientosService.inactivar(desplazamiento.id).subscribe({
        next: () => {
          Swal.fire("Éxito", "Desplazamiento inactivado.", "success");
          this.cargarDesplazamientos();
        },
        error: () => Swal.fire("Error", "No se pudo inactivar el desplazamiento.", "error"),
      });
    });
  }

  activar(desplazamiento: PesvDesplazamientos): void {
    Swal.fire({
      title: "¿Activar desplazamiento?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, activar",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (!result.isConfirmed || !desplazamiento.id) return;
      this.desplazamientosService.activar(desplazamiento.id).subscribe({
        next: () => {
          Swal.fire("Éxito", "Desplazamiento activado.", "success");
          this.cargarDesplazamientos();
        },
        error: () => Swal.fire("Error", "No se pudo activar el desplazamiento.", "error"),
      });
    });
  }

  // ============ Exportar ============
  exportarExcel(tableId: string, nameFile: string): void {
    Utils.exportVisibleTableToExcel(tableId, nameFile);
  }

  exportToPDF(tableId: string, nameFile: string, titleFile: string): void {
    Utils.exportToPDF(tableId, nameFile, titleFile);
  }

  // ============ Ver detalles relacionados ============
  verRiesgos(desplazamientos: PesvDesplazamientos): void {
    if (!desplazamientos.id) return;
    this.router.navigate(['listado/riesgos-desplazamientos', desplazamientos.id]);
  }

  // Ver puntos críticos relacionados
  verPuntosCriticos(desplazamientos: PesvDesplazamientos): void {
    if (!desplazamientos.id) return;
    this.router.navigate(['listado/puntos-criticos', desplazamientos.id]);
  }

  // Ver requisitos relacionados
  verRequisitos(desplazamientos: PesvDesplazamientos): void {
    if (!desplazamientos.id) return;
    this.router.navigate(['listado/requisitos-desplazamientos', desplazamientos.id]);
  }

  // Ver seguimientos relacionados
  verSeguimientos(desplazamientos: PesvDesplazamientos): void {
    if (!desplazamientos.id) return;
    this.router.navigate(['listado/seguimientos-desplazamientos', desplazamientos.id]);
  }

  // Ver novedades relacionadas
  verNovedades(desplazamientos: PesvDesplazamientos): void {
    if (!desplazamientos.id) return;
    this.router.navigate(['listado/novedades-desplazamientos', desplazamientos.id]);
  }

  // Ver cierre relacionado
  verCierre(desplazamientos: PesvDesplazamientos): void {
    if (!desplazamientos.id) return;
    this.router.navigate(['listado/cierre-desplazamientos', desplazamientos.id]);
  }
}